import { createContext, type ComponentChildren } from 'preact';
import { useContext, useMemo } from 'preact/hooks';
import createI18nInstance, {
  type CustomFormatters,
  type I18NInstance
} from '@/utils/createI18nInstance';

const TranslationsContext = createContext<I18NInstance<any> | undefined>(
  undefined
);

export type TranslationsProviderProps<Translations> = {
  children: ComponentChildren;
  locale: string;
  config: Translations;
  defaults: Translations;
  customFormatters?: CustomFormatters;
};

export function TranslationsProvider<Translations>({
  children,
  locale,
  config,
  defaults,
  customFormatters
}: TranslationsProviderProps<Translations>) {
  const i18nInstance = useMemo(
    () => createI18nInstance(defaults, config, locale, customFormatters),
    [locale, defaults, config, customFormatters]
  );

  return (
    <TranslationsContext.Provider value={i18nInstance}>
      {children}
    </TranslationsContext.Provider>
  );
}

export default function useTranslations<
  Translations
>(): I18NInstance<Translations> {
  const i18nInstance = useContext(TranslationsContext);

  if (!i18nInstance) {
    throw new Error('useTranslations must be used within TranslationsProvider');
  }
  return i18nInstance as I18NInstance<Translations>;
}
